import type { EmployeeSummary } from "@/hooks/useMakersBoardData";
import { useRoles } from "@/hooks/useRoles";
import { isBambooFallbackRole } from "@/lib/role-formatting";
import { useOffices } from "@/contexts/OfficesContext";
import { useUserTimeZone } from "@/hooks/useUserTimeZone";
import { computeWeeklyAvailability } from "@/utils/availabilityUtils";
import { computeWeekColumns, type WeekColumn } from "@/utils/weekUtils";
import { currentWeekStartInTimeZone, toISODateString } from "@/utils/dateUtils";
import { useEffect, useMemo, useRef, useState } from "react";
import { MonDateInput } from "@/components/WeekStepper/WeekStepper";
import { RoleFilter } from "@/components/filters/RoleFilter";
import { AvailabilityModeToggle } from "@/components/common/AvailabilityModeToggle";
import type { AvailabilityMode } from "@/utils/makersUtils";
import { AvailabilityTimeline } from "./AvailabilityTimeline";
import { useOoo } from "@/contexts/OooContext";

interface AvailabilityBoardProps {
  employees: EmployeeSummary[];
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  defaultMinAvailableHours?: number;
  minAvailableHoursByWeek?: Map<string, number>;
  showMinHoursControl?: boolean;
  defaultAvailabilityMode?: AvailabilityMode;
  defaultSelectedRoles?: string[];
  resetFiltersKey?: string;
  compact?: boolean;
  onEmployeeClick?: (employeeId: string) => void;
}

const DEFAULT_NUM_WEEKS = 13;
const DEFAULT_MIN_AVAILABLE_HOURS = 40;

export function AvailabilityBoard({
  employees,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  defaultMinAvailableHours = DEFAULT_MIN_AVAILABLE_HOURS,
  minAvailableHoursByWeek,
  showMinHoursControl = true,
  defaultAvailabilityMode = "all",
  defaultSelectedRoles,
  resetFiltersKey,
  compact = false,
  onEmployeeClick,
}: AvailabilityBoardProps) {
  const { selectedOffices } = useOffices();
  const { roles } = useRoles();
  const { oooByEmployee } = useOoo();
  const userTimeZone = useUserTimeZone();

  const defaultStart = useMemo(
    () => toISODateString(currentWeekStartInTimeZone(userTimeZone)),
    [userTimeZone],
  );
  const defaultEnd = useMemo(() => {
    const s = new Date((startDate ?? defaultStart) + "T00:00:00Z");
    const end = new Date(s.getTime() + DEFAULT_NUM_WEEKS * 7 * 24 * 60 * 60 * 1000);
    return toISODateString(end);
  }, [defaultStart, startDate]);

  const [internalStartDateStr, setInternalStartDateStr] = useState(startDate ?? defaultStart);
  const [internalEndDateStr, setInternalEndDateStr] = useState(endDate ?? defaultEnd);
  const [minAvailableHours, setMinAvailableHours] = useState(defaultMinAvailableHours);
  const [minHoursInput, setMinHoursInput] = useState(String(defaultMinAvailableHours));
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>(defaultAvailabilityMode);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const startDateStr = startDate ?? internalStartDateStr;
  const endDateStr = endDate ?? internalEndDateStr;
  const setStartDateStr = onStartDateChange ?? setInternalStartDateStr;
  const setEndDateStr = onEndDateChange ?? setInternalEndDateStr;
  const roleOptions = useMemo(() => roles.map((r) => r.id), [roles]);

  useEffect(() => {
    setInternalStartDateStr(defaultStart);
    setInternalEndDateStr(defaultEnd);
  }, [defaultStart, defaultEnd]);

  // Apply the default role filter once roles load: every role except the bamboo
  // fallback (Atom).
  const appliedDefaultRoles = useRef(false);
  useEffect(() => {
    if (appliedDefaultRoles.current || roles.length === 0) return;
    setSelectedRoles(
      defaultSelectedRoles ?? roles.filter((r) => !isBambooFallbackRole(r)).map((r) => r.id)
    );
    appliedDefaultRoles.current = true;
  }, [defaultSelectedRoles, roles]);

  useEffect(() => {
    if (resetFiltersKey === undefined) return;

    setMinAvailableHours(defaultMinAvailableHours);
    setMinHoursInput(String(defaultMinAvailableHours));
    setAvailabilityMode(defaultAvailabilityMode);
    setSelectedRoles(
      defaultSelectedRoles ?? roles.filter((r) => !isBambooFallbackRole(r)).map((r) => r.id)
    );
  }, [resetFiltersKey, defaultMinAvailableHours, defaultAvailabilityMode, defaultSelectedRoles, roles]);

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const clearRoleFilter = () => setSelectedRoles([]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      if (emp.is_active === false) return false;
      if (selectedOffices.length > 0 && selectedOffices[0] !== "all") {
        if (!emp.office_id || !selectedOffices.includes(emp.office_id)) return false;
      }
      if (selectedRoles.length > 0) {
        const empRoles = emp.role ?? [];
        if (empRoles.length === 0 || !empRoles.some((r) => selectedRoles.includes(r))) return false;
      }
      return true;
    });
  }, [employees, selectedOffices, selectedRoles]);

  const minAvailableHoursForWeek = useMemo(() => {
    if (!minAvailableHoursByWeek) return minAvailableHours;

    return (week: WeekColumn) => {
      return Math.max(minAvailableHours, minAvailableHoursByWeek.get(toISODateString(week.start)) ?? 0);
    };
  }, [minAvailableHours, minAvailableHoursByWeek]);

  const weekData = useMemo(() => {
    const start = new Date(startDateStr + "T00:00:00Z");
    const end = new Date(endDateStr + "T00:00:00Z");
    const diffMs = end.getTime() - start.getTime();
    const numWeeks = Math.max(1, Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1);
    const weeks = computeWeekColumns(start, numWeeks);
    return computeWeeklyAvailability(filteredEmployees, weeks, minAvailableHoursForWeek, availabilityMode);
  }, [filteredEmployees, startDateStr, endDateStr, minAvailableHoursForWeek, availabilityMode]);

  return (
    <div className={`availability-board${compact ? " availability-board--compact" : ""}`}>
      <div className="availability-controls">
        <label className="availability-controls__field">
          <span className="availability-controls__label">From</span>
          <MonDateInput
            value={startDateStr}
            onChange={setStartDateStr}
            fontSize="0.8rem"
            autoFocus={false}
          />
        </label>
        <label className="availability-controls__field">
          <span className="availability-controls__label">To</span>
          <MonDateInput
            value={endDateStr}
            onChange={setEndDateStr}
            fontSize="0.8rem"
            autoFocus={false}
          />
        </label>
        {showMinHoursControl ? (
          <label className="availability-controls__field">
            <span className="availability-controls__label">Min hours</span>
            <input
              type="number"
              className="availability-controls__input"
              value={minHoursInput}
              min={0}
              step={1}
              onChange={(e) => {
                const raw = e.target.value;
                setMinHoursInput(raw);
                if (raw === "") return;
                const parsed = parseInt(raw);
                if (!isNaN(parsed) && parsed >= 0) {
                  setMinAvailableHours(parsed);
                }
              }}
              onBlur={() => {
                const parsed = parseInt(minHoursInput);
                if (minHoursInput === "" || isNaN(parsed) || parsed < 0) {
                  setMinHoursInput(String(minAvailableHours));
                }
              }}
            />
          </label>
        ) : null}
        <div className="availability-controls__field">
          <span className="availability-controls__label">Show</span>
          <AvailabilityModeToggle mode={availabilityMode} onChange={setAvailabilityMode} />
        </div>
        <div className="availability-controls__roles">
          <RoleFilter
            roles={roleOptions}
            selected={selectedRoles}
            onToggle={toggleRole}
            onClear={clearRoleFilter}
          />
        </div>
      </div>
      <AvailabilityTimeline
        weekData={weekData}
        oooByEmployee={oooByEmployee}
        onEmployeeClick={onEmployeeClick}
      />
    </div>
  );
}
